import { useState, useEffect } from 'react';
import ReactPlayer from 'react-player/vimeo';
import { Typography, Button } from 'antd';
import { PlayCircleOutlined, ArrowRightOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import "./cac.scss";
import { CacVideos } from '../cacVideos/cacVideos.component';
import ProductTypeCode from '../../enums/productTypeCode.enum';
import { useContext } from 'react';
import { UserContext } from '../../contexts/user.context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FlowType from '../../enums/flowType.enum';
import useWidth from '../../hooks/useWidth.hook';
import Breakpoint from '../../enums/breakpoint.enum';
import PosthogHelper from '../../helpers/posthog.helper';
import { FacilitySearch } from '../facilitySearch/facilitySearch.component';
import { listFaqs } from '../../services/faq.service';
import { FaqCode } from '../../enums/index.enum';
import Questions from '../questions/questions.component';


const { Paragraph, Text } = Typography;

export const Cac = () => {
  const width = useWidth()
  const { currentUser } = useContext(UserContext)
  const [expandedParagraphs, setExpandedParagraphs] = useState({});
  const [videosDrawerVisible, setVideosDrawerVisible] = useState(false);
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [faqs, setFaqs] = useState([])
  useEffect(() => {
    document.title = `Instalab | Coronary Calcium Scan`
    autoOpenVideos()
  }, [])

  useEffect(() => {
    const fetchFaqs = async () => {
      const faqs = await listFaqs({filter: {code: {$in: [
        FaqCode.CAC_WHAT_IS, 
        FaqCode.CAC_EFFECTIVENESS, 
        FaqCode.CAC_FREQUENCY, 
        FaqCode.CAC_BLOOD_TESTING, 
        FaqCode.CAC_CCTA, 
        FaqCode.CAC_LONGEVITY]}}})
      setFaqs(faqs)
    }
    fetchFaqs()
  }, [])

  const autoOpenVideos = () => {
    const video = searchParams.get('video')
    if (video === 'true') {
      setVideosDrawerVisible(true)
    }
  }
  
  const showVideosDrawer = () => {
    setVideosDrawerVisible(true);
  };
  
  const closeVideosDrawer = () => {
    setVideosDrawerVisible(false);
  };
  
  // Toggle paragraph expansion
  const toggleParagraph = (id) => {
    setExpandedParagraphs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  
  // Define paragraph content with short and full versions
  const howContent = [
    {
      id: 'how-1',
      title: `The Scan: Measuring Calcified Plaque in Minutes`,
      short: "A coronary artery calcium (CAC) scan is a fast, non-invasive test that detects calcium buildup in the arteries of your heart—an early indicator of atherosclerosis and your future risk of heart disease.",
      full: <>
        A coronary artery calcium (CAC) scan is a fast, non-invasive test that detects calcium buildup in the arteries of your heart—an early indicator of atherosclerosis and your future risk of heart disease.
        <br/><br/>
        Small electrodes are placed on your chest to monitor your heart rate. You’ll lie on a motorized table that gently slides into a CT scanner. You’ll be asked to hold your breath for short intervals while the machine takes high-resolution images of your heart.
        <br/><br/>
        The scan itself takes just about 10 minutes, and you’ll be done in under 1 hour total, including prep time.
      </>,
      img: "/img/cac/procedure.jpg"
    },
    {
      id: 'how-2',
      title: "Image Analysis: Understanding Your CAC Score",
      short: "Once your scan is complete, the images are analyzed to calculate your Coronary Artery Calcium (CAC) score—a numerical value that reflects the total amount of calcified plaque in your coronary arteries.",
      full: <>
        Once your scan is complete, the images are analyzed to calculate your Coronary Artery Calcium (CAC) score—a numerical value that reflects the total amount of calcified plaque in your coronary arteries.
        <br/><br/>
        This score is a direct measure of plaque burden and a powerful predictor of your future heart attack and stroke risk. A score of zero means no detectable calcified plaque and a low short-term risk. Higher scores indicate more plaque and a greater likelihood of cardiovascular disease.
        <br/><br/>
        Your results are reviewed by a physician and delivered in a report that includes your absolute score and how it compares to others of the same age and sex. This helps determine whether lifestyle changes, medications, or further testing are recommended.
      </>,
      img: "/img/cac/results.jpg"
    },
  ];

  return (
    <div className="cac">
      <CacVideos
        visible={videosDrawerVisible} 
        onClose={closeVideosDrawer}
        width={900}
        placement="right"
        title={width <= Breakpoint.MD ? "Everything You Need to Know" : "Coronary CTA: Everything You Need to Know"}
      />

      <div className="cac-hero">
        <div className="cac-section">
          <Paragraph className="cac-h1">
            Coronary Calcium Scan
          </Paragraph>
          <Paragraph className="cac-p1">
            Scanning the arteries in your heart for calcium buildup. This calcium is a sign of plaque, which can narrow or block your arteries, increasing your risk of a heart attack.
          </Paragraph>
          <Button
            type="primary"
            className="schedule-cac-btn"
            onClick={() => {
              PosthogHelper.track(currentUser, `[cac] clicked schedule cac`, true)
              navigate(`/flow/${FlowType.CAC}`)
            }}
          >
            <span className="schedule-cac-btn-text">Schedule</span> <ArrowRightOutlined className="schedule-cac-btn-icon" />
          </Button>

          <br />

          <Paragraph className="cac-quote">
            "The CAC scan is one of the most underused but most <i>powerful</i> tools available for long-term heart health and prevention. It should be a standard screening test, much like colonoscopies, mammograms, or pap smears. We routinely screen for those conditions because they can develop silently over time. Heart disease is no different."
          </Paragraph>

          <div 
            className="cac-hero-img-container"
            onClick={showVideosDrawer}
          >
            <img 
              src="/img/cac/steven.jpg" 
              className="cac-hero-img" 
            />

            <PlayCircleOutlined className="cac-play-icon" />

            <div className="cac-video-attribute">
              <Paragraph className="cac-video-attribute-name"> Dr. Steven Winiarski </Paragraph>
              <Paragraph className="cac-video-attribute-title"> Clinical Lipidologist, Longevity Medicine </Paragraph>
            </div>
          </div>
        </div>
      </div>
      
      <div className="how-section">
        <div className="cac-section">
          <div className="divider-container">
            <Text className="cac-h1"> 
              How It Works
            </Text>
          </div>

          {howContent.map((how) => (
            <div key={how.id} className="paragraph-container">
              {how.title && (
                <Paragraph className={`cac-p1 bold`}>
                  {how.title}
                </Paragraph>
              )}
              
              <Paragraph className="cac-p2">
                {expandedParagraphs[how.id] ? how.full : how.short}
              </Paragraph>
              
              {how.short !== how.full && (
                <Button 
                  type="link" 
                  className="read-more-btn"
                  onClick={() => toggleParagraph(how.id)}
                  icon={expandedParagraphs[how.id] ? <UpOutlined /> : <DownOutlined />}
                >
                  {expandedParagraphs[how.id] ? "Read Less" : "Read More"}
                </Button>
              )}

              <br/>

              {how.img && (
                <img src={how.img} className="cac-how-img" />
              )}

              {how.video && (
                <div className="cac-how-video-container">
                  <ReactPlayer
                    url={how.video}
                    playing={true}
                    loop={true}
                    muted={true}
                    playsinline={true}
                    controls={false}
                    width="100%"
                    height="auto"
                    className="cac-how-video"
                    config={{
                      vimeo: {
                        playerOptions: {
                          responsive: true,
                          background: true
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-section">
        <div className="cac-section">
          <div className="divider-container">
            <Text className="cac-h1"> 
              Pricing
            </Text>
          </div>
          <Paragraph className="cac-p1 pricing-p1">
            We'll help you find the most convenient and affordable testing location near you and handle the appointment scheduling. Enter your location below to search our database.
          </Paragraph>

          <FacilitySearch
            productTypeCodes={[ProductTypeCode.CTCALCIUM, ProductTypeCode.CONCIERGE_FEE]}
            flowType={FlowType.CAC}
          />
        </div>
      </div>

      {faqs?.length > 0 && (
        <div className="faq-section">
          <div className="cac-section">
            <div className="divider-container">
              <Text className="cac-h1"> 
              FAQ
            </Text>
          </div>

            <Questions items={faqs} />
          </div>
        </div>
      )}
    </div>
  );
} 